// apps/web/src/pages/compras/fornecedores-page.tsx — Fase 13, Plano 17 (COMP-01, D-18).
// Lista e manutenção de fornecedores. Fornecedor "A confirmar" (criado pela NF) sobe ao topo por ordem do servidor.
// "Inativar fornecedor" é auditado mas não destrutivo; a regra de pedido em aberto é do servidor (a tela só antecipa o aviso).
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, MoreHorizontal, Plus, RefreshCw, Search, UserCheck, XCircle } from 'lucide-react';
import type { FornecedorResumo, FornecedorView, ListaFornecedores, SituacaoFornecedor } from '@pieltec/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../components/ui/toast';
import { reativarFornecedor } from '../../lib/compras/reativar-fornecedor';
import { contagemFornecedores, formatarCnpj, mensagemErroFornecedor } from '../../lib/compras/cnpj';
import {
  acoesDoFornecedor,
  apresentacaoSituacaoFornecedor,
  bloqueioInativacao,
  filtrosFornecedoresAtivos,
  filtrosFornecedoresVazios,
  montarQueryFornecedores,
  totalDePaginas,
  type FiltrosFornecedores,
} from '../../lib/compras/fornecedores';
import { apiMutate } from '../../lib/csrf-fetch';
import { obterSessaoAtual } from '../../lib/session';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { FornecedorSheet } from './fornecedor-sheet';
import { CampoSelect } from '../../components/ui/campo-select';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ICONES_SITUACAO = { UserCheck, CheckCircle2, XCircle } as const;
const SITUACOES: ReadonlyArray<{ valor: SituacaoFornecedor; rotulo: string }> = [
  { valor: 'ATIVO', rotulo: 'Ativo' },
  { valor: 'A_CONFIRMAR', rotulo: 'A confirmar' },
  { valor: 'INATIVO', rotulo: 'Inativo' },
];

export function FornecedoresPage() {
  const toast = useToast();
  const [aReativar, setAReativar] = useState<FornecedorResumo | null>(null);
  const [reativando, setReativando] = useState(false);
  const [erroReativar, setErroReativar] = useState<string | null>(null);
  const reativacaoEmCurso = useRef(false);
  const [dados, setDados] = useState<ListaFornecedores | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [filtros, setFiltros] = useState<FiltrosFornecedores>(filtrosFornecedoresVazios);
  const [pagina, setPagina] = useState(1);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [aInativar, setAInativar] = useState<FornecedorResumo | null>(null);
  const [inativando, setInativando] = useState(false);
  const [erroInativar, setErroInativar] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/compras/fornecedores?${montarQueryFornecedores(filtros, pagina)}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaFornecedores;
        if (!sinal || sinal.vivo) setDados(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  const podeGerenciar = permissoes.includes('compras:gerenciar');

  function alterarFiltros(parcial: Partial<FiltrosFornecedores>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterarFiltros({ busca: buscaDigitada.trim() });
  }

  function limparFiltros(): void {
    setBuscaDigitada('');
    setFiltros(filtrosFornecedoresVazios());
    setPagina(1);
  }

  function abrirNovo(): void {
    setEmEdicao(null);
    setSheetAberto(true);
  }

  function abrirEdicao(fornecedor: FornecedorResumo): void {
    setEmEdicao(fornecedor.id);
    setSheetAberto(true);
  }

  function pedirInativacao(fornecedor: FornecedorResumo): void {
    setErroInativar(bloqueioInativacao(fornecedor.pedidosEmAberto));
    setAInativar(fornecedor);
  }

  async function confirmarInativacao(): Promise<void> {
    if (!aInativar || bloqueioInativacao(aInativar.pedidosEmAberto)) return;
    setInativando(true);
    setErroInativar(null);
    try {
      const res = await apiMutate(`/api/v1/compras/fornecedores/${encodeURIComponent(aInativar.id)}/inativar`, {});
      if (!res.ok) {
        const falha = await lerErroApi(res, 'Não foi possível inativar o fornecedor.');
        setErroInativar(mensagemErroFornecedor(falha.codigo) ?? falha.mensagem);
        if (falha.codigo === 'FORNECEDOR_COM_PEDIDO_ABERTO') await carregar();
        return;
      }
      setAInativar(null);
      await carregar();
    } catch {
      setErroInativar('Não foi possível inativar o fornecedor. Verifique a conexão e tente novamente.');
    } finally {
      setInativando(false);
    }
  }

  function aoSalvar(_fornecedor: FornecedorView): void {
    void carregar();
  }

  async function confirmarReativacao(): Promise<void> {
    if (!aReativar || !podeGerenciar || aReativar.situacao !== 'INATIVO' || reativacaoEmCurso.current) return;
    reativacaoEmCurso.current = true;
    setReativando(true);
    setErroReativar(null);
    try {
      const mensagem = await reativarFornecedor(aReativar.id);
      if (mensagem) {
        setErroReativar(mensagem);
        toast.error({ title: 'Não foi possível reativar o fornecedor.', description: mensagem });
        return;
      }
      toast.success({ title: 'Fornecedor reativado.', description: aReativar.razaoSocial });
      setAReativar(null);
      await carregar();
    } finally {
      reativacaoEmCurso.current = false;
      setReativando(false);
    }
  }

  const total = dados?.total ?? 0;
  const comFiltro = filtrosFornecedoresAtivos(filtros);
  const paginas = totalDePaginas(total, dados?.tamanhoPagina ?? 50);
  const mostrarErro = erro;
  const mostrarSkeleton = !erro && carregando && dados === null;
  const vazio = !erro && !mostrarSkeleton && total === 0 && !comFiltro;
  const semResultado = !erro && !mostrarSkeleton && total === 0 && comFiltro;
  const mostrarLista = !erro && !mostrarSkeleton && total > 0;
  const comAcoes = podeGerenciar && (dados?.itens.some((f) => acoesDoFornecedor(f, permissoes).length > 0) ?? false);
  const bloqueio = aInativar ? bloqueioInativacao(aInativar.pedidosEmAberto) : null;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Fornecedores</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {dados ? contagemFornecedores(dados.total) : ' '}
            </p>
          </div>
          {podeGerenciar && (
            <Button type="button" onClick={abrirNovo}>
              <Plus aria-hidden="true" />
              Novo fornecedor
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              type="search"
              aria-label="Buscar fornecedores"
              placeholder="Buscar por razão social, nome fantasia ou CNPJ"
              value={buscaDigitada}
              onChange={(e) => setBuscaDigitada(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary" disabled={mostrarSkeleton}>
            Buscar
          </Button>
        </form>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="filtro-situacao-fornecedor" className="text-xs font-medium text-slate-600">
              Situação
            </label>
            <CampoSelect
              id="filtro-situacao-fornecedor"
              value={filtros.situacao}
              onValueChange={(valor) => alterarFiltros({ situacao: valor as FiltrosFornecedores['situacao'] })}
              options={[{ value: '', label: 'Todas as situações' }, ...SITUACOES.map((situacao) => ({ value: situacao.valor, label: situacao.rotulo }))]}
              className="w-56"
            />
          </div>

          <label htmlFor="filtro-inativos" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox id="filtro-inativos" checked={filtros.incluirInativos} onCheckedChange={(marcada) => alterarFiltros({ incluirInativos: marcada === true })} />
            Incluir inativos
          </label>

          <Button
            type="button"
            variant={filtros.situacao === 'A_CONFIRMAR' ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={filtros.situacao === 'A_CONFIRMAR'}
            onClick={() => alterarFiltros({ situacao: filtros.situacao === 'A_CONFIRMAR' ? '' : 'A_CONFIRMAR' })}
          >
            <UserCheck aria-hidden="true" />A confirmar
          </Button>

          {comFiltro && (
            <Button type="button" variant="ghost" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {mostrarErro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar os fornecedores. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : mostrarSkeleton ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando fornecedores">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : vazio ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum fornecedor cadastrado</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Cadastre um fornecedor ou importe uma NF de compra: o emitente vira fornecedor automaticamente.
            </p>
            {podeGerenciar && (
              <Button type="button" onClick={abrirNovo}>
                <Plus aria-hidden="true" />
                Novo fornecedor
              </Button>
            )}
          </CardContent>
        </Card>
      ) : semResultado ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum fornecedor encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : mostrarLista ? (
        <Card className="min-w-0 gap-0 overflow-hidden py-0">
          <div className="min-w-0 overflow-x-auto" role="region" aria-label="Tabela de fornecedores" tabIndex={0}>
          <table className="w-full text-sm">
            <caption className="sr-only">Fornecedores</caption>
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className={CABECALHO}>Razão social</th>
                <th scope="col" className={CABECALHO}>CNPJ</th>
                <th scope="col" className={`${CABECALHO} hidden md:table-cell`}>Contato</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Pedidos em aberto</th>
                <th scope="col" className={CABECALHO}>Situação</th>
                {comAcoes && (
                  <th scope="col" className="w-12 px-1 py-2">
                    <span className="sr-only">Ações</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {dados?.itens.map((fornecedor) => {
                const situacao = apresentacaoSituacaoFornecedor(fornecedor.situacao);
                const IconeSituacao = ICONES_SITUACAO[situacao.icone];
                const acoes = acoesDoFornecedor(fornecedor, permissoes);
                return (
                  <tr key={fornecedor.id} className={`border-t hover:bg-slate-50 ${fornecedor.situacao === 'INATIVO' ? 'text-muted-foreground' : ''}`}>
                    <td className="max-w-[24rem] px-3 py-2" title={fornecedor.razaoSocial}>
                      <p className="truncate font-semibold">{fornecedor.razaoSocial}</p>
                      {fornecedor.nomeFantasia && <p className="truncate text-xs text-muted-foreground">{fornecedor.nomeFantasia}</p>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{formatarCnpj(fornecedor.cnpj)}</td>
                    <td className="hidden max-w-[16rem] truncate px-3 py-2 md:table-cell">{fornecedor.contato ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fornecedor.pedidosEmAberto}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
                        <IconeSituacao className="size-3.5 shrink-0" aria-hidden="true" />
                        {situacao.rotulo}
                      </span>
                    </td>
                    {comAcoes && (
                      <td className="w-12 px-1 py-1 text-right">
                        {acoes.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações do fornecedor ${fornecedor.razaoSocial}`} className="size-11 sm:size-9">
                                <MoreHorizontal aria-hidden="true" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {acoes.includes('editar') && (
                                <DropdownMenuItem className="min-h-10" onSelect={() => abrirEdicao(fornecedor)}>
                                  {fornecedor.situacao === 'A_CONFIRMAR' ? 'Confirmar fornecedor' : 'Editar'}
                                </DropdownMenuItem>
                              )}
                              {acoes.includes('inativar') && (
                                <DropdownMenuItem className="min-h-10" onSelect={() => pedirInativacao(fornecedor)}>
                                  Inativar fornecedor
                                </DropdownMenuItem>
                              )}
                              {acoes.includes('reativar') && (
                                <DropdownMenuItem className="min-h-10" onSelect={() => { setErroReativar(null); setAReativar(fornecedor); }}>
                                  Reativar fornecedor
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <nav aria-label="Paginação dos fornecedores" className="flex items-center justify-between gap-3 border-t bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
            <span aria-live="polite">
              Página {pagina} de {paginas}
            </span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={pagina <= 1 || carregando} onClick={() => setPagina((p) => Math.max(1, p - 1))}>
                <ChevronLeft aria-hidden="true" />
                Anterior
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={pagina >= paginas || carregando} onClick={() => setPagina((p) => p + 1)}>
                Próxima
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          </nav>
        </Card>
      ) : null}

      <FornecedorSheet aberto={sheetAberto} onAbertoChange={setSheetAberto} fornecedorId={emEdicao} aoSalvar={aoSalvar} />

      <AlertDialog open={aReativar !== null} onOpenChange={(aberto) => !aberto && !reativacaoEmCurso.current && setAReativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reativar fornecedor</AlertDialogTitle>
            <AlertDialogDescription>
              {aReativar ? `"${aReativar.razaoSocial}" volta a aparecer nas buscas e fica disponível para novos pedidos. O histórico é mantido.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {erroReativar && <div role="alert"><FormMessage>{erroReativar}</FormMessage></div>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reativando}>Voltar</AlertDialogCancel>
            <AlertDialogAction disabled={reativando} onClick={(event) => { event.preventDefault(); void confirmarReativacao(); }}>
              {reativando ? 'Reativando...' : 'Reativar fornecedor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={aInativar !== null} onOpenChange={(aberto) => !aberto && !inativando && setAInativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar fornecedor</AlertDialogTitle>
            <AlertDialogDescription>
              {aInativar ? `"${aInativar.razaoSocial}" deixa de aparecer nas buscas e nos pedidos novos. O histórico é mantido.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {erroInativar && (
            <div role="alert">
              <FormMessage>{erroInativar}</FormMessage>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inativando}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={inativando || bloqueio !== null}
              onClick={(event) => {
                event.preventDefault();
                void confirmarInativacao();
              }}
            >
              {inativando ? 'Inativando...' : 'Inativar fornecedor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
